import React, { useState, useEffect, useRef } from "react";
import {
  Form,
  Button,
  Container,
  Spinner,
  Alert,
} from "react-bootstrap";
import "./JTNewEntry.css";
import { useLocation, useNavigate } from "react-router-dom";
import JTSegmentDetails from "./JTSegmentDetails";
import JTFormFields from "./JTFormFields";
import api from '../services/api';
import ChecklistModal from './ChecklistModal';
import { Workbook } from 'exceljs';
import { saveAs } from 'file-saver';

const JTNewEntry = () => {
  const segmentDetailsRef = useRef();
  const originalStatusesRef = useRef({});
  const location = useLocation();
  const navigate = useNavigate();
  const { jobNumber, editMode: initialEditMode, segmentName: initialSegmentName } = location.state || {};

  const [formData, setFormData] = useState({
    unit: "",
    year: "",
    month: "",
    project_name: "",
    job_number: "",
    client: "",
    no_of_segments: 1, // Default to 1 segment
  });

  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];

  const [segmentsData, setSegmentsData] = useState([]);
  const [currentSegmentIndex, setCurrentSegmentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({}); // For validation errors
  const [showErrorAlert, setShowErrorAlert] = useState(false); // To control Alert visibility

  const [isEditMode, setIsEditMode] = useState(initialEditMode || false); // Separate state for editMode

  const [downloadingSummary, setDownloadingSummary] = useState(false);

  // Checklist modal state
  const [checklistModal, setChecklistModal] = useState(null); // { segment, statusStage, previouslySkipped }
  const pendingSubmitRef = useRef(null); // Stores the continuation function after checklist
  const [checklistFilled, setChecklistFilled] = useState(false);

  // Default fields for each segment (Include no_of_segments)
  const defaultSegmentFields = {
    no_of_segments: 1, // Will be updated based on position
    unit: "",
    year: "",
    month: "",
    project_name: "",
    job_number: "",
    client: "",
    segment_name: "",
    completed_month: "",
    data_correction_done: "No", // Default to "No"
    status: "",
    research_group_head: "",
    contact_person_in_research: "",
    analysis_group_head: "",
    contact_person_for_scripting: "",
    contact_person_for_dp: "",
    expected_date_of_link: null,
    actual_date_of_link: null,
    expected_date_of_final_output: null,
    actual_date_of_final_output: null,
    fw_end_date: null,
    fw_start_date: null,
    ap_received_date: null,
    qnr_received_date: null,
    latest_topline_date: null,
    final_awn_date: null,
    date_of_awn: null,
    check_list_followed: "No", // Default to "No"
    feedback_survey_done: "No", // Default to "No"
    remarks: "",
    archive_taken: "No", // Default to "No"
    loi: "",
    number_of_responses: "",
    oe_costing: "",
    oe_coding_done: "No", // Default to "No"
    no_of_top_lines: "",
    final_processed_samples: null,
    achieved_sample_size: null,
    actual_sample_size: null,
    advanced_analytics: "",
    norms_done: "No", // Default to "No"
    survey_platform: "",
    dp_platform: "",
    backup_taken: "No", // Default to "No"
    backup_folder_path: "",
    project_classification: "",
  };

  useEffect(() => {
    const fetchProjectData = async () => {
      setLoading(true);

      if (isEditMode && jobNumber) {
        try {
          const response = await api.get(
            `api/segments/job/${encodeURIComponent(jobNumber)}/`,
            {
              headers: {
                Authorization: `Token ${localStorage.getItem("Token")}`,
              },
            }
          );

          const projectData = response.data;

          setFormData({
            unit: projectData.unit || "",
            year: projectData.year || "",
            month: projectData.month || "",
            project_name: projectData.project_name || projectData.projectName || "", // Ensure correct key
            job_number: projectData.job_number || projectData.jobNumber || "", // Ensure correct key
            client: projectData.client || "",
            no_of_segments: projectData.segments?.length || 1,
          });

          // Ensure segment name is correctly populated
          const segments = projectData.segments?.map((segment, index) => ({
            ...defaultSegmentFields,
            ...segment,
            no_of_segments: index + 1,
            status: segment.status || 'Open',
          })) || [];
          setSegmentsData(segments);

          // Jump directly to the segment that was clicked in the project list
          if (initialSegmentName) {
            const targetIndex = segments.findIndex(
              (seg) => seg.segment_name === initialSegmentName
            );
            if (targetIndex !== -1) setCurrentSegmentIndex(targetIndex);
          }

          // Store original statuses so we only trigger survey flow on actual status change
          const origStatuses = {};
          segments.forEach((seg, idx) => { origStatuses[idx] = seg.status || ''; });
          originalStatusesRef.current = origStatuses;

        } catch (error) {
          console.error("Failed to fetch project data:", error);
          alert("Failed to fetch project data. Please check the console for details.");
        } finally {
          setLoading(false);
        }
      } else {
        // Create mode: initialize empty segments with all fields
        const initialSegments = Array.from({ length: formData.no_of_segments }, (_, index) => ({
          ...defaultSegmentFields,
          no_of_segments: index + 1, // Assign no_of_segments based on position
        }));
        setSegmentsData(initialSegments);
        setLoading(false);
      }
    };

    fetchProjectData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, jobNumber]);

  // Check if checklist has been filled for the current segment
  useEffect(() => {
    const segId = segmentsData[currentSegmentIndex]?.id;
    if (!segId) { setChecklistFilled(false); return; }
    api.get(`api/checklists/status/?segment_id=${segId}`, {
      headers: { Authorization: `Token ${localStorage.getItem('Token')}` },
    }).then(res => {
      const data = res.data || {};
      const filled = Object.values(data).some(stage => stage && !stage.skipped);
      setChecklistFilled(filled);
    }).catch(() => setChecklistFilled(false));
  }, [currentSegmentIndex, segmentsData]);

  // Dynamically update segments when no_of_segments changes in create-new and edit modes
  useEffect(() => {
    if (isEditMode) {
      const currentLength = segmentsData.length;
      const desiredLength = formData.no_of_segments;

      if (desiredLength > currentLength) {
        // Add new segments with default fields
        const additionalSegments = Array.from(
          { length: desiredLength - currentLength },
          (_, index) => ({
            ...defaultSegmentFields,
            no_of_segments: currentLength + index + 1, // Assign correct no_of_segments
          })
        );
        setSegmentsData([...segmentsData, ...additionalSegments]);
      } else if (desiredLength < currentLength) {
        // Remove excess segments
        setSegmentsData(segmentsData.slice(0, desiredLength));
        // Adjust currentSegmentIndex if necessary
        if (currentSegmentIndex >= desiredLength) {
          setCurrentSegmentIndex(desiredLength - 1);
        }
      } else {
        // If no_of_segments is unchanged, ensure no_of_segments in each segment is correct
        const updatedSegments = segmentsData.map((segment, index) => ({
          ...segment,
          no_of_segments: index + 1,
        }));
        setSegmentsData(updatedSegments);
      }
    } else {
      // Create mode: Initialize segments based on no_of_segments
      const initialSegments = Array.from({ length: formData.no_of_segments }, (_, index) => ({
        ...defaultSegmentFields,
        no_of_segments: index + 1, // Assign no_of_segments based on position
      }));
      setSegmentsData(initialSegments);
      setCurrentSegmentIndex(0); // Reset to the first segment
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.no_of_segments, isEditMode]);

  const handleSegmentNumberChange = (value) => {
    const newSegmentNumber = parseInt(value, 10) || 1;

    setFormData((prevFormData) => ({
      ...prevFormData,
      no_of_segments: newSegmentNumber,
    }));
  };

 
// AND REPLACE THE ENTIRE THING WITH THIS:
const handleSegmentChange = (field, value) => {
    // 1. Create a new array to avoid direct state mutation.
    const updatedSegments = [...segmentsData];
  
    // 2. Create a new object for the segment being changed.
    const updatedSegment = {
      ...updatedSegments[currentSegmentIndex], // Copy all old properties
      [field]: value,                          // Overwrite/add the one that changed
    };
  
    // 3. Handle special logic for specific fields using the new object.
    if (field === "actual_date_of_final_output" && value) {
      const date = new Date(value);
      if (!isNaN(date)) {
        // Add the completed_month to our new object
        updatedSegment.completed_month = months[date.getMonth()];
      }
    }
  
    // Handle numeric fields that can be null
    if (["final_processed_samples", "actual_sample_size", "achieved_sample_size"].includes(field)) {
      // Modify the property on our new object
      updatedSegment[field] = value === "" || isNaN(Number(value)) ? null : Number(value);
    }
  
    // 4. Place the new, updated object back into the new array.
    updatedSegments[currentSegmentIndex] = updatedSegment;
    
    // 5. Set the state with the new array. This will now work correctly.
    setSegmentsData(updatedSegments);
};

  const validateForm = () => {
    const newErrors = {};
  
    // Validate main form fields
    if (!formData.month) newErrors.month = "Month is required.";
    if (!formData.unit) newErrors.unit = "Unit is required.";
    if (!formData.project_name) newErrors.project_name = "Project Name is required.";
  
    // Validate year
    if (!formData.year) {
      newErrors.year = "Year is required.";
    } else if (!/^\d{4}$/.test(formData.year)) {
      newErrors.year = "Year must be a 4-digit number.";
    }
  
    // Validate segment_name for the current segment
    const currentSegment = segmentsData[currentSegmentIndex];
    if (!currentSegment.segment_name || currentSegment.segment_name.trim() === "") {
      newErrors["segment_name"] = "Segment name is required.";
    }
  
    // Only apply mandatory validation if status is "Closed"
    if (currentSegment.status === "Closed") {
      const mandatoryFields = [
        "unit", "year", "month", "project_name", "job_number", "segment_name",
        "data_correction_done", "status", "research_group_head", "contact_person_in_research",
        "analysis_group_head", "contact_person_for_scripting", "contact_person_for_dp",
        "expected_date_of_link", "actual_date_of_link", "expected_date_of_final_output",
        "actual_date_of_final_output", "fw_end_date", "fw_start_date", "ap_received_date",
        "qnr_received_date", "check_list_followed", "feedback_survey_done", "archive_taken",
         "no_of_top_lines", "final_processed_samples",
        "achieved_sample_size", "actual_sample_size", "advanced_analytics", "norms_done",
        "survey_platform", "dp_platform", "backup_taken",  "project_classification",
      ];
  
      for (const field of mandatoryFields) {
        const value = currentSegment[field];
  
        if (
          value === null || value === undefined ||
          (typeof value === "string" && value.trim() === "")
        ) {
          newErrors[field] = `${field.replace(/_/g, " ")} is required when project is closed.`;
        }
      }
    }
  
    setErrors(newErrors);
  
    // Show Alert if there are errors
    if (Object.keys(newErrors).length > 0) {
      setShowErrorAlert(true);
    } else {
      setShowErrorAlert(false);
    }
  
    return Object.keys(newErrors).length === 0;
  };
  

  const handleSubmit = async () => {
    // Ensure the form is valid before submitting
    if (!validateForm()) return;

    console.log("Segments Data Before Submission:", segmentsData);

    // Separate existing segments (those with ids) and new segments (those without ids)
    const existingSegments = segmentsData.filter((segment) => segment.id);
    const newSegments = segmentsData.filter((segment) => !segment.id);

    // Check if any existing segment is transitioning to a checklist-trigger status
    const CHECKLIST_STATUSES = ['Test Link Delivered', 'Final Link Delivered'];
    for (let i = 0; i < existingSegments.length; i++) {
      const seg = existingSegments[i];
      const originalStatus = originalStatusesRef.current[segmentsData.indexOf(seg)] ?? '';
      const newStatus = seg.status || '';
      const scriptingInCharge = seg.contact_person_for_scripting;
      const scriptingApplicable = scriptingInCharge && scriptingInCharge !== 'Not Applicable';
      if (CHECKLIST_STATUSES.includes(newStatus) && newStatus !== originalStatus && scriptingApplicable) {
        // Determine the status_stage key
        const statusStage = newStatus === 'Test Link Delivered'
          ? 'test_link_delivered'
          : 'final_link_delivered';
        // Check if test link stage was previously skipped (when moving to Final Link)
        let previouslySkipped = false;
        if (statusStage === 'final_link_delivered') {
          try {
            const res = await api.get(`api/checklists/status/?segment_id=${seg.id}`, {
              headers: { Authorization: `Token ${localStorage.getItem('Token')}` },
            });
            const testData = res.data?.test_link_delivered;
            previouslySkipped = testData ? testData.skipped : true; // skipped or never filled
          } catch (_) {
            previouslySkipped = true;
          }
        }
        // Store the continuation function and show the modal
        pendingSubmitRef.current = async () => {
          await _doSave(existingSegments, newSegments);
        };
        setChecklistModal({ segment: seg, statusStage, previouslySkipped });
        return; // Pause — modal will call pendingSubmitRef after submission
      }
    }

    await _doSave(existingSegments, newSegments);
  };

  const _doSave = async (existingSegments, newSegments) => {
    try {
      if (isEditMode) {
        if (existingSegments.length > 0) {
          const cleanedSegments = existingSegments.map((segment) =>
            Object.fromEntries(Object.entries(segment).filter(([_, value]) => value !== null))
          );

          const payload = {
            ...formData,
            created_by: localStorage.getItem("username"),
            segments: cleanedSegments,
          };

          await api.patch(
            `api/segments/${encodeURIComponent(jobNumber)}/update-segments/`,
            payload,
            {
              headers: {
                Authorization: `Token ${localStorage.getItem("Token")}`,
              },
            }
          );
        }

        if (newSegments.length > 0) {
          for (const newSegment of newSegments) {
            const payload = {
              ...formData,
              segments: [newSegment],
            };
            await api.post("api/segments/", payload, {
              headers: {
                Authorization: `Token ${localStorage.getItem("Token")}`,
              },
            });
          }
        }
      } else {
        const payload = {
          ...formData,
          created_by: localStorage.getItem("username"),
          segments: segmentsData,
        };
        await api.post("api/segments/", payload, {
          headers: {
            Authorization: `Token ${localStorage.getItem("Token")}`,
          },
        });
      }

      // Trigger survey flow if status is "Closed" and feedback not yet done
      if (segmentDetailsRef.current) {
        const currentStatus = segmentDetailsRef.current.formData?.status;
        const feedbackSurveyDone = segmentDetailsRef.current.formData?.feedback_survey_done;

        if (currentStatus === "Closed" && feedbackSurveyDone !== "Yes") {
          await segmentDetailsRef.current.handleStatusChange(currentStatus, feedbackSurveyDone);
          return;
        }
      }

      alert("Project saved successfully.");
      navigate("/projects_list");

    } catch (error) {
      console.error("Error:", error.response?.data || error.message);
      // Backend blocked Closed status because feedback survey not done → go to survey
      if (error.response?.data?.error === 'feedback_required' && segmentDetailsRef.current) {
        const currentStatus = segmentDetailsRef.current.formData?.status;
        const feedbackSurveyDone = segmentDetailsRef.current.formData?.feedback_survey_done;
        await segmentDetailsRef.current.handleStatusChange(currentStatus, feedbackSurveyDone);
        return;
      }
      alert(`Failed to save project: ${error.response?.data?.detail || error.message}`);
    }
  };

  // Scroll to the top when showErrorAlert changes to true
  useEffect(() => {
    if (showErrorAlert) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [showErrorAlert]);

  const handleChecklistDownload = async () => {
    const segId = segmentsData[currentSegmentIndex]?.id;
    if (!segId) return;
    try {
      const response = await api.get(`api/checklists/download/?segment_id=${segId}`, {
        responseType: 'blob',
        headers: { Authorization: `Token ${localStorage.getItem('Token')}` },
      });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', `Checklist_${segmentsData[currentSegmentIndex].segment_name || segId}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (_) {
      alert('No checklist submissions found for this segment.');
    }
  };

  const handleAwnDownload = async () => {
    const segId = segmentsData[currentSegmentIndex]?.id;
    if (!segId) return;
    try {
      const res = await api.get(`api/awn/entries/?segment_id=${segId}`, {
        headers: { Authorization: `Token ${localStorage.getItem('Token')}` },
      });
      const entries = Array.isArray(res.data) ? res.data : res.data?.results || [];
      if (!entries.length) { alert('No AWN found for this segment.'); return; }
      const awn = entries[0];

      const templateRes = await fetch('/4SiGHT_Analysis_Warning_Note.xlsx');
      if (!templateRes.ok) throw new Error('Template not found');
      const arrayBuffer = await templateRes.arrayBuffer();

      const workbook = new Workbook();
      await workbook.xlsx.load(arrayBuffer);
      const ws = workbook.worksheets[0];

      const fmtDate = (d) => {
        if (!d) return '';
        try {
          const dt = new Date(d);
          return `${String(dt.getDate()).padStart(2,'0')}-${dt.toLocaleString('en-GB',{month:'short'})}-${dt.getFullYear()}`;
        } catch { return ''; }
      };
      const setR = (cell, val) => { ws.getCell(cell).value = val; ws.getCell(cell).alignment = { horizontal: 'right' }; };

      const opts = [awn.option1 && '☑95%', awn.option2 && '☑90%', awn.option3 && '☑80%', awn.option4 && '☑67%'].filter(Boolean);

      ws.getCell('C6').value = awn.submissionDate || '';
      ws.getCell('C7').value = awn.jobNumber || formData.job_number || '';
      ws.getCell('C8').value = awn.projectName || formData.project_name || '';
      ws.getCell('C12').value = formData.client || '';
      ws.getCell('C9').value = awn.projectType || '';
      ws.getCell('C10').value = awn.researcher_name || '';
      ws.getCell('C11').value = awn.researcherCost || '';
      setR('C14', fmtDate(awn.finalQuestionnaire));
      ws.getCell('C15').value = awn.scirptingNeeded;
      ws.getCell('F15').value = awn.complexLogic;
      ws.getCell('C16').value = awn.sampleSize;
      ws.getCell('C17').value = awn.dataCollection;
      ws.getCell('C18').value = awn.haveS2CSub;
      setR('C26', fmtDate(awn.firstCutDate));
      ws.getCell('C28').value = awn.dataWeightingRequired;
      ws.getCell('C30').value = awn.sigTesting;
      ws.getCell('C32').value = awn.spssOutput;
      setR('F26', fmtDate(awn.finalOutput));
      setR('F28', fmtDate(awn.weightingMatrix));
      ws.getCell('F32').value = awn.statementFF;
      ws.getCell('C19').value = awn.quota;
      setR('C20', fmtDate(awn.quotaDetails));
      setR('C21', fmtDate(awn.expectedFieldDate));
      ws.getCell('E21').value = 'Expected Final Link';
      setR('F21', fmtDate(awn.expectedFinalLinkDate));
      setR('C22', fmtDate(awn.analysisPlan));
      ws.getCell('E30').value = opts.join(', ');
      ws.getCell('C35').value = awn.maxdiffProgramming;
      ws.getCell('C37').value = awn.numberOfAttributes;
      ws.getCell('C42').value = awn.jaccards;
      ws.getCell('C43').value = awn.regression;
      ws.getCell('C44').value = awn.krushal;
      ws.getCell('C45').value = awn.shapely;
      ws.getCell('C46').value = awn.correlation;
      ws.getCell('C47').value = awn.chaid;
      ws.getCell('C49').value = awn.cluster;
      ws.getCell('C50').value = awn.factor;
      ws.getCell('C51').value = awn.bIP;
      ws.getCell('F42').value = awn.dDV;
      ws.getCell('F44').value = awn.dIV;

      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
        `4SiGHT_Analysis_Warning Note_${awn.projectName || formData.project_name}.xlsx`);
    } catch (err) {
      console.error('AWN download failed:', err);
      alert(`Failed to download AWN: ${err?.message || err}`);
    }
  };

  if (loading) {
    return (
      <Container className="text-center my-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading project data...</p>
      </Container>
    );
  }

  return (
    <Container fluid className="px-4 py-2">
      <Form>
        {/* Edit-mode icon toolbar — sticky at the top */}
        {isEditMode && (
          <div style={{
            position: 'sticky', top: 0, zIndex: 100,
            background: '#fff', borderBottom: '1px solid #e0e0e0',
            padding: '6px 0 8px', display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12,
          }}>
            <Button
              variant="primary"
              onClick={handleSubmit}
              title="Save Changes"
              style={{ width: 38, height: 38, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17 3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V7l-4-4zm-5 16c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3zm3-10H5V5h10v4z"/>
              </svg>
            </Button>
            {segmentsData[currentSegmentIndex]?.id && (
              <>
                <Button
                  variant="outline-secondary"
                  onClick={handleChecklistDownload}
                  disabled={!checklistFilled}
                  title={checklistFilled ? 'Download Checklist (Excel)' : 'Checklist not yet filled'}
                  style={{ width: 38, height: 38, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 14H7v-2h5v2zm5-4H7v-2h10v2zm0-4H7V7h10v2z"/>
                  </svg>
                </Button>
                <Button
                  variant="outline-secondary"
                  onClick={handleAwnDownload}
                  title="Download AWN (Excel)"
                  style={{ width: 38, height: 38, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19 9h-4V3H9v6H5l7 7 7-7zm-8 2V5h2v6h1.17L12 13.17 9.83 11H11zm-6 8v2h14v-2H5z"/>
                  </svg>
                </Button>
                <Button
                  variant={downloadingSummary ? 'outline-info' : 'outline-secondary'}
                  disabled={downloadingSummary}
                  onClick={async () => {
                    const segId = segmentsData[currentSegmentIndex]?.id;
                    if (!segId || downloadingSummary) return;
                    setDownloadingSummary(true);
                    try {
                      const res = await api.get(`api/closure-summary/?segment_id=${segId}`, {
                        responseType: 'blob',
                        headers: { Authorization: `Token ${localStorage.getItem('Token')}` },
                      });
                      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                      const link = document.createElement('a');
                      link.href = url;
                      link.setAttribute('download', `Closure_Summary_${segId}.pdf`);
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                      window.URL.revokeObjectURL(url);
                    } catch (err) {
                      alert('Failed to download closure summary.');
                    } finally {
                      setDownloadingSummary(false);
                    }
                  }}
                  title={downloadingSummary ? 'Generating PDF…' : 'Download Closure Summary (PDF)'}
                  style={{ width: 38, height: 38, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: downloadingSummary ? 'wait' : 'pointer' }}
                >
                  {downloadingSummary ? (
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#0d6efd" strokeWidth="2.5" strokeLinecap="round"
                      style={{ animation: 'spin 1s linear infinite' }}>
                      <circle cx="12" cy="12" r="10" stroke="#bee3f8" strokeWidth="2.5" />
                      <path d="M12 2 a10 10 0 0 1 10 10" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M20 2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-8.5 7.5c0 .83-.67 1.5-1.5 1.5H9v2H7.5V7H10c.83 0 1.5.67 1.5 1.5v1zm5 2c0 .83-.67 1.5-1.5 1.5h-2.5V7H15c.83 0 1.5.67 1.5 1.5v3zm4-3H19v1h1.5V11H19v2h-1.5V7h3v1.5zM9 9.5h1v-1H9v1zM4 6H2v14c0 1.1.9 2 2 2h14v-2H4V6zm10 5.5h1v-3h-1v3z"/>
                    </svg>
                  )}
                </Button>
                {downloadingSummary && (
                  <span style={{ fontSize: 11.5, color: '#0d6efd', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
                    Generating PDF…
                  </span>
                )}
                <Button
                  variant="outline-secondary"
                  onClick={() => navigate(`/changelog/${segmentsData[currentSegmentIndex].id}`)}
                  title="Change Log"
                  style={{ width: 38, height: 38, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19 3h-4.18C14.4 1.84 13.3 1 12 1s-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm2 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
                  </svg>
                </Button>
              </>
            )}
          </div>
        )}
        {showErrorAlert && (
          <Alert
            variant="danger"
            onClose={() => setShowErrorAlert(false)}
            dismissible
          >
            <Alert.Heading>Form Submission Failed</Alert.Heading>
            <ul className="mb-0">
              {Object.values(errors).map((error, idx) => (
                <li key={idx}>{error}</li>
              ))}
            </ul>
          </Alert>
        )}

        <JTFormFields
          formData={formData}
          setFormData={setFormData}
          handleSegmentNumberChange={handleSegmentNumberChange}
          months={months}
          errors={errors}
          isEditMode={isEditMode} // Pass isEditMode as a separate prop
        />

        <h4 className="mt-4">Segment Details</h4>
        {segmentsData.length > 0 && (
          <JTSegmentDetails
            formData={segmentsData[currentSegmentIndex]}
            handleChange={handleSegmentChange}
            ref={segmentDetailsRef}
            errors={errors} // Pass errors to JTSegmentDetails
            segmentIndex={currentSegmentIndex} // Pass the current segment index
          />
        )}

        <div className="d-flex justify-content-between mt-3">
          <Button
            variant="secondary"
            onClick={() => setCurrentSegmentIndex((prev) => Math.max(prev - 1, 0))}
            disabled={currentSegmentIndex === 0}
            style={{ width: 40, padding: 0 }}
          >
            ←
          </Button>
          <Button
            variant="secondary"
            onClick={() => setCurrentSegmentIndex((prev) => Math.min(prev + 1, segmentsData.length - 1))}
            disabled={currentSegmentIndex === segmentsData.length - 1}
            style={{ width: 40, padding: 0 }}
          >
            →
          </Button>
        </div>

        {!isEditMode && (
          <div className="mt-3">
            <Button variant="primary" onClick={handleSubmit} style={{ width: '100%' }}>
              Create Project
            </Button>
          </div>
        )}
      </Form>

      {checklistModal && (
        <ChecklistModal
          show={!!checklistModal}
          onHide={() => {}} // backdrop="static" — no close without action
          segment={checklistModal.segment}
          statusStage={checklistModal.statusStage}
          previouslySkipped={checklistModal.previouslySkipped}
          onSubmitted={async () => {
            setChecklistModal(null);
            if (pendingSubmitRef.current) {
              const fn = pendingSubmitRef.current;
              pendingSubmitRef.current = null;
              await fn();
            }
          }}
        />
      )}
    </Container>
  );
};

export default JTNewEntry;
